import { NakedButton } from '@cb/apricot-react';
import { Box, Grid } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FORM_ERROR_MAPPING } from 'src/constants';
import { TCustomFieldProperty } from 'src/types';

import FormField from './FormField';
import Typography from './Typography';

interface MultiInstanceProps {
  comment?: string;
  label: string;
  path: string;
  maxEntries?: number;
  readOnly?: boolean;
  required?: boolean;
  addButton?: string;
  deleteButton?: string;
  instanceLabel?: string;
  variant?: 'standard' | 'outlined';
  properties: TCustomFieldProperty[];
  inputLabelClass?: string;
}

const MultiInstance: React.FC<MultiInstanceProps> = ({
  comment,
  label,
  path,
  maxEntries,
  required,
  variant,
  addButton,
  deleteButton,
  instanceLabel,
  properties,
}) => {
  const {
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();

  const value = watch(path);
  const hasError = Boolean(_.get(errors, path));
  const errorType = _.get(errors, `${path}.type`) as unknown as string;
  const errorMessage = errorType ? FORM_ERROR_MAPPING[errorType] : undefined;

  const [instances, setInstances] = useState<number[]>([0]);

  useEffect(() => {
    if (!value) return;
    setInstances(new Array(value.length || 1).fill(0).map((_value, index) => index));
    // eslint-disable-next-line
  }, [path]);

  const handleClickAddMoreInstance = () => {
    setInstances([...instances, instances.length]);
  };

  const handleClickDelete = () => {
    setInstances(instances.slice(0, instances.length - 1));
    if (value && Array.isArray(value)) {
      setValue(path, value.slice(0, instances.length - 1));
    }
  };

  const renderInstance = (index: number) => (
    <Grid key={index} item xs={12}>
      <Grid container spacing={3}>
        {instanceLabel && (
          <Grid item xs={12}>
            <Typography variant="body1">{`${instanceLabel} ${index + 1}`}</Typography>
          </Grid>
        )}
        {properties.map((property) => (
          <Grid key={property.path} item xs={property.xs} lg={property.lg}>
            <FormField
              key={property.path}
              field={{
                ...property,
                required: required && property.required,
                label: property.label || '',
                path: [path, index, property.path].join('.'),
              }}
              required={required && property.required}
              variant={variant}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );

  const shouldShowDeleteButton = deleteButton && instances.length >= 2;
  const shouldShowAddButton = !maxEntries || instances.length < maxEntries;

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Typography hasError={hasError} variant="body1">
          {`${label} ${required ? '*' : ''}`}
        </Typography>
      </Grid>

      {errorMessage && (
        <Grid item xs={12}>
          <Typography hasError={hasError} variant="body1">
            {errorMessage}
          </Typography>
        </Grid>
      )}

      <Grid container spacing={3}>
        {instances.map((index) => renderInstance(index))}
      </Grid>

      {comment && (
        <Grid item xs={12}>
          <Typography variant="body1">{comment}</Typography>
        </Grid>
      )}

      {(shouldShowDeleteButton || shouldShowAddButton) && (
        <Grid container justifyContent="flex-end">
          {shouldShowAddButton && (
            <NakedButton onClick={handleClickAddMoreInstance}>
              <Box display="flex" alignItems="center">
                <AddCircleOutlineIcon />
                &nbsp;
                {addButton}
              </Box>
            </NakedButton>
          )}

          {shouldShowDeleteButton && (
            <NakedButton onClick={handleClickDelete}>
              <Box display="flex" alignItems="center">
                <RemoveCircleOutlineIcon />
                &nbsp;
                {deleteButton}
              </Box>
            </NakedButton>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default MultiInstance;
